/* eslint-disable */
import { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import useUiToasts, { SUCCESS } from '../stores/ToastStore';
import useUiAlerts, { createAlertConfig } from '../stores/AlertsStore';
import { handleSaveError, useGenericError } from './FormErrorService';
import { useDataService } from './ApiDataService';
import { useNavigate } from '../utilities/PgmUtilities';
import { useSuccessfulAction } from '../utilities/FormUtilities';

const successToastObject = createAlertConfig(SUCCESS, i18n._(t`Saved Successfully`));

const SERVICE_TESTS_UPDATE_URL_WITH_PATCH_METHOD = '/ngp/v2/service-tests/';
const TESTS_CACHE_ENDPOINT = '/ipm/v1/open/tests/cache';

export function useFormSubmitWrapper() {
    const [, uiToastActions] = useUiToasts();
    const [, uiAlertActions] = useUiAlerts();
    const navigate = useNavigate();
    const handleGenericError = useGenericError();
    const [saving, setSaving] = useState(false);

    const navigateAway = useCallback(
        (goTo) => {
            if (_.isEmpty(goTo)) {
                return;
            }

            return navigate(goTo);
        },
        [navigate]
    );

    const submitWrapper = useCallback(
        (submitFunction, onSubmitGoTo) => {
            return (formData, e) => {
                if (e) {
                    e.preventDefault();
                }

                return Promise.resolve(setSaving(true))
                    .then(() => uiAlertActions.handleRemove())
                    .then(() => submitFunction(formData))
                    .then((response) => {
                        uiToastActions.createToast(
                            _.merge(successToastObject, {
                                createOnNavigate: !_.isEmpty(onSubmitGoTo),
                            })
                        );
                        return response;
                    })
                    .then(() => {
                        navigateAway(onSubmitGoTo);
                    })
                    .catch(handleGenericError)
                    .finally(() => setSaving(false));
            };
        },
        [handleGenericError, navigateAway, uiAlertActions, uiToastActions]
    );

    return {
        saving,
        submitWrapper,
    };
}

export function useBulkActionWrapper(successMessage) {
    const [, uiAlertActions] = useUiAlerts();
    const handleGenericError = useGenericError();
    const handleSuccessfulAction = useSuccessfulAction(successMessage);
    const [working, setWorking] = useState(false);

    const bulkActionWrapper = useCallback((callerConfig) => {
        const emptyRefresherFunc = _.noop;
        const funcConfig = _.defaults(callerConfig, {
            postBulkActionHandler: (response) =>
                handleSuccessfulAction(
                    response,
                    callerConfig.onBulkActionGoTo,
                    callerConfig.refresherFunc || emptyRefresherFunc
                ),
            errorHandler: handleGenericError,
        });

        return () => {
            return Promise.resolve(setWorking(true))
                .then(() => uiAlertActions.handleRemove())
                .then(funcConfig.bulkActionFunction)
                .then(funcConfig.postBulkActionHandler)
                .catch(funcConfig.errorHandler)
                .finally(() => setWorking(false));
        };
    }, []);

    return {
        working,
        bulkActionWrapper,
    };
}

export default function FormSaveServiceFactory(dataService) {
    /**
     * @param {object} [callerConfig]
     * @param {string} callerConfig.url
     * @param {string} callerConfig.recordId
     * @param {object} callerConfig.dataToSave
     * @param {import('./FormErrorService').FieldMapper} callerConfig.validationFieldMapper
     * @param {(e: any, details: object) => void} callerConfig.setError
     * @param {handleSaveError} callerConfig.saveErrorHandler
     * @returns {Promise}
     */
    function save(callerConfig) {
        const funcConfig = _.defaults(callerConfig, {
            preSaveHandler: (response) => response,
            saveErrorHandler: handleSaveError,
            isV3Api: false,
        });

        let func;
        if (callerConfig.saveFunc) {
            func = callerConfig.saveFunc;
        } else {
            if (funcConfig.isV3Api) {
                func = _.isEmpty(funcConfig.recordId)
                    ? (readyData) => dataService.post(`${funcConfig.url}/create`, readyData)
                    : (readyData) => dataService.post(`${funcConfig.url}/${funcConfig.recordId}/modify`, readyData);
            } else {
                func = _.isEmpty(funcConfig.recordId)
                    ? (readyData) => dataService.post(`${funcConfig.url}`, readyData)
                    : SERVICE_TESTS_UPDATE_URL_WITH_PATCH_METHOD === funcConfig.url
                    ? (readyData) =>
                          dataService
                              .patch(`${funcConfig.url}/${funcConfig.recordId}`, readyData)
                              .then(() => dataService.get(TESTS_CACHE_ENDPOINT, { t: Date.now() }))
                    : (readyData) => dataService.put(`${funcConfig.url}/${funcConfig.recordId}`, readyData);
            }
        }

        return Promise.resolve()
            .then(() => funcConfig.preSaveHandler(funcConfig.dataToSave))
            .then(func)
            .catch((error) =>
                funcConfig.saveErrorHandler(error, funcConfig.setError, funcConfig.validationFieldMapper)
            );
    }

    function bulkActionSelection(
        url,
        params,
        tableSelection,
        bulkChanges = {},
        postBulkActionHandler = (response) => response
    ) {
        return dataService
            .post(
                url,
                _.extend({}, bulkChanges, {
                    selectionParams: tableSelection,
                }),
                params
            )
            .then(postBulkActionHandler);
    }

    return { save, bulkActionSelection };
}

export function useFormSaveService() {
    const dataService = useDataService();
    return useMemo(() => FormSaveServiceFactory(dataService), [dataService]);
}
